import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatDate, formatDateTime } from '../../utils/format';
import { STATUS_LABEL } from '../../utils/laptops';

// The Laptop tab on a person's profile (People page): their assigned laptop,
// what PDQ Connect says about it, and their history. Read-only; assigning,
// moving and returning are done on the Laptops page, which opens on this
// person from the button here.

const STATUS_TINT = {
  assigned: 'bg-emerald-50 text-emerald-700',
  suggested: 'bg-blue-50 text-blue-700',
  missing: 'bg-amber-100 text-amber-700',
  exempt: 'bg-gray-100 text-gray-600',
};

function Item({ label, children }) {
  return (
    <div>
      <div className="text-[7px] font-bold text-gray-500 uppercase">{label}</div>
      <div className="text-[10px]">{children || '-'}</div>
    </div>
  );
}

export default function PersonLaptopTab({ personId, onOpenLaptops }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    api.get(`laptops/${personId}`).then(setData).catch(e => setError(e.message || 'Could not load the laptop.'));
  }, [personId]);

  if (error) return <div className="text-[10px] text-red-600">{error}</div>;
  if (!data) return <div className="text-[10px] text-gray-400">Loading…</div>;

  const p = data.person;
  const l = p?.laptop;
  const d = l?.device;

  return (
    <div data-tour="person-laptop" className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          {l ? <div className="text-[12px] font-bold text-navy">{l.asset_tag}</div>
            : <div className="text-[10px] text-gray-600">{p?.status === 'exempt' ? `Does not need a laptop: ${p.exempt_reason}` : 'No laptop assigned.'}</div>}
          {p && <span className={`inline-flex mt-0.5 px-1.5 py-px rounded-md text-[9px] font-semibold ${STATUS_TINT[p.status] || ''}`}>{STATUS_LABEL[p.status]}</span>}
        </div>
        {onOpenLaptops && (
          <button onClick={onOpenLaptops}
            className="px-2 py-1 rounded text-[10px] font-semibold border border-gray-200 bg-white text-gray-700 whitespace-nowrap">
            {l ? 'Change on Laptops' : 'Assign on Laptops'}
          </button>
        )}
      </div>

      {l && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4">
          <Item label="Model">{l.model}</Item>
          <Item label="Serial Number">{l.serial_number}</Item>
          <Item label="Assigned">{`${formatDate(l.assigned_date)}${l.assigned_by ? ` by ${l.assigned_by}` : ''}`}</Item>
          <Item label="Last Seen by PDQ">
            {d ? (d.gone ? 'No longer in PDQ' : (d.last_checkin_at ? formatDateTime(d.last_checkin_at) : 'Not yet seen')) : (data.pdq.configured ? 'Not in PDQ' : 'PDQ not connected')}
            {d?.online ? ' · online now' : ''}
          </Item>
          <Item label="Last Signed-in User">{d?.last_user}</Item>
          <Item label="Operating System">{d?.os_name}</Item>
        </div>
      )}

      {p?.flags?.length > 0 && (
        <div>{p.flags.map((f, i) => <div key={i} className="text-[9px] text-red-600">{f.text}</div>)}</div>
      )}
      {p?.suggestion && (
        <div className="text-[9px] text-blue-700">PDQ suggests {p.suggestion.name}{p.suggestion.model ? ` (${p.suggestion.model})` : ''}, which they sign in to.</div>
      )}
      {p?.others?.length > 0 && (
        <div className="text-[9px] text-gray-500">Also signs in to {p.others.map(o => o.name).join(', ')}.</div>
      )}
      {data.to_recover.map((r, i) => (
        <div key={i} className="text-[9px] text-red-600">
          {r.asset_tag} is still with them having left ({r.source === 'assigned' ? 'assigned on Ops' : 'last signed in to by them, per PDQ'}).
        </div>
      ))}

      <div className="border-t border-gray-100 pt-2">
        <div className="text-[8px] font-bold text-gray-500 uppercase mb-1.5">History</div>
        {data.history.length === 0 ? <div className="text-[9px] text-gray-500">No laptop recorded on Ops yet.</div> : (
          <ol className="divide-y divide-gray-100">
            {data.history.map(h => (
              <li key={h.id} className="py-1 text-[9px] text-gray-600">
                <span className="font-semibold text-navy text-[10px]">{h.asset_tag}</span>
                {' '}assigned {formatDate(h.assigned_date)}{h.assigned_by ? ` by ${h.assigned_by}` : ''}
                {h.returned_date ? `, returned ${formatDate(h.returned_date)}${h.returned_by ? ` (${h.returned_by})` : ''}` : ', current'}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
